import { Link, useMatchRoute, CatchBoundary } from "@tanstack/react-router";
import {
  Tabs,
  TabsContent,
  TabsContentProps,
  TabsList,
  TabsTrigger,
} from "../../ui/tabs";
import React from "react";
import { cn, render, Renderable } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { ChevronDown } from "lucide-react";
import { LuCheck } from "react-icons/lu";
import { CircleButton } from "../circle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ErrorComponent } from "@/state/error-component";
import { useNested } from "../table-view/nested";
import { useCssObserver } from "@/hooks/use-css-observer";
import { useTooltipProps } from "../tooltip";

export type TabItem = {
  href?: string;
  search?: any;
  path?: string;
  title: string;
  disabled?: boolean;
  requiredPermissions?: string[];
  className?: string;
  tooltip?: string;
};

export type TabsContentPartialProps = Omit<TabsContentProps, "value">;

export type TabsProps = {
  exact?: boolean;
  root: string;
  items: TabItem[];
  row?: Renderable;
  rowProps?: React.HTMLProps<HTMLDivElement>;
  header?: string | React.ReactNode;
  full?: boolean;
  padding?: boolean;
  content?: TabsContentPartialProps;
  children?: React.ReactNode;
  showAsDropdown?: boolean;
  dropdownLabel?: string;
  retainSubRoute?: boolean;
  subRouteDepth?: number;
};

export type NavTabProps = TabsProps &
  Omit<React.ComponentPropsWithRef<typeof Tabs>, "selected" | "content">;

function sliceToAtMostNthSlash(str: string, n: number) {
  let count = 0;
  for (let i = 0; i < str.length; i++) {
    if (str[i] === "/") {
      count++;
      if (count === n) {
        return str.slice(0, i); // slice up to the x’th slash
      }
    }
  }
  // fewer than n slashes → return whole string
  return str;
}

export default function NavTabs({
  exact,
  items: _items,
  root,
  header,
  row,
  rowProps,
  full = true,
  padding = true,
  children,
  className,
  content,
  isAlt,
  showAsDropdown = false,
  dropdownLabel,
  retainSubRoute,
  subRouteDepth,
  ...props
}: NavTabProps) {
  const tooltipProps = useTooltipProps();
  const navTabsRef = React.useRef<HTMLDivElement | null>(null);
  const navTabsContentRef = React.useRef<HTMLDivElement | null>(null);
  useCssObserver({
    target: navTabsRef,
    storage: navTabsContentRef,
    mapper: {
      "--tabs-height": (entry) => `${entry.contentRect.height + 0}px`,
    },
  });
  const nested = useNested();
  const matchRoute = useMatchRoute();
  const { items, selected, subRoute } = React.useMemo(() => {
    let selected: string | undefined;
    let subRoute: string | undefined;
    const items = _items.map((item) => {
      const href = item.href ?? `${root}/${item.path}`;
      const match = matchRoute({ to: href, fuzzy: true });
      if (match) {
        selected = href;
      }
      if (match?.["**"]) {
        subRoute = match?.["**"];
      }
      if (subRoute && subRouteDepth) {
        subRoute = sliceToAtMostNthSlash(subRoute, subRouteDepth);
      }
      return {
        ...item,
        href,
        match,
      };
    });
    return { items, selected, subRoute };
  }, [_items, root, subRouteDepth]);
  const show = !exact || matchRoute({ to: selected });
  const applySubRoute = React.useCallback(
    (to: string) => {
      if (!retainSubRoute) {
        return to;
      }
      // const subRoute = matchRoute({ to: selected })?.["**"];
      if (subRoute) {
        return [
          to.endsWith("/") ? to.slice(0, to.length - 1) : to,
          subRoute,
        ].join("/");
      }
      return to;
    },
    [retainSubRoute, retainSubRoute ? subRoute : undefined],
  );
  const ref = React.useRef<HTMLButtonElement | null>(null);
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }
    ref.current?.scrollIntoView({
      behavior: "instant",
      block: "nearest",
      inline: "nearest",
    });
  }, [ref.current]);
  React.useEffect(() => {
    const handler = () => {
      if (!ref.current) {
        return;
      }
      ref.current?.scrollIntoView({
        behavior: "instant",
        block: "nearest",
        inline: "nearest",
      });
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, []);
  return (
    <Tabs
      data-component="nav-tabs"
      {...props}
      isAlt={isAlt}
      className={cn(
        "@container/tabs",
        children !== undefined && full && "h-full",
        className,
      )}
      value={selected}
    >
      {show && (
        <div ref={navTabsRef}>
          {header && (
            <div
              data-component="nav-tabs-header"
              className="text-2xl font-medium leading-7"
            >
              {header}
            </div>
          )}
          <div
            className={cn(
              "flex flex-row gap-4 items-center relative w-full",
              // !isAlt && "pb-1",
            )}
          >
            <ScrollArea
              orientation="horizontal"
              type="hover"
              className={cn(!isAlt && "pb-3 h-lg:pb-4 pt-2 h-lg:pt-3")}
            >
              {showAsDropdown && dropdownLabel && (
                <div className="flex items-center gap-2 py-2 text-sm font-normal">
                  {dropdownLabel}
                </div>
              )}
              <TabsList data-component="nav-tabs-list">
                {showAsDropdown && (
                  <div className="flex items-center gap-2 px-3 py-2 text-sm font-medium">
                    {items.find((item) => item.href === selected)?.title}
                  </div>
                )}
                {items.map(
                  ({
                    path,
                    href,
                    search = true,
                    title,
                    disabled,
                    className,
                    tooltip,
                  }) => (
                    <TabsTrigger
                      key={path}
                      value={href}
                      asChild={true}
                      disabled={disabled && disabled === true ? true : false}
                      ref={(el) => {
                        if (selected === href) {
                          ref.current = el;
                        }
                      }}
                      className={cn(
                        showAsDropdown
                          ? "hidden"
                          : selected === href
                            ? undefined
                            : "supports-[container]:hidden supports-[container]:@sm/tabs:w-auto supports-[container]:@sm/tabs:inline-flex",
                        className,
                      )}
                      {...tooltipProps}
                      data-tooltip-html={tooltip}
                    >
                      <Link to={applySubRoute(href)} search={search}>
                        {title}
                      </Link>
                    </TabsTrigger>
                  ),
                )}
                <DropdownMenu data-component="nav-tabs-dropdown">
                  <DropdownMenuTrigger asChild>
                    <CircleButton
                      size={showAsDropdown ? "sm" : "xs"}
                      className={cn(
                        showAsDropdown
                          ? "flex mr-1"
                          : "hidden supports-[container]:flex supports-[container]:@sm/tabs:hidden mr-1",
                      )}
                    >
                      {showAsDropdown ? (
                        <ChevronDown />
                      ) : (
                        <BiDotsHorizontalRounded />
                      )}
                    </CircleButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {items.length > 1 &&
                      items.map(({ path, href, search, title, disabled }) => (
                        <DropdownMenuItem key={path}>
                          {showAsDropdown ? (
                            <div className="flex items-center justify-between w-full">
                              <Link
                                to={applySubRoute(href)}
                                search={search}
                                className="w-full"
                              >
                                {title}
                              </Link>
                              {selected === href && (
                                <LuCheck className="h-4 w-4" />
                              )}
                            </div>
                          ) : (
                            <TabsTrigger
                              isAlt={false}
                              value={href}
                              asChild={true}
                              disabled={
                                disabled && disabled === true ? true : false
                              }
                              className="justify-start"
                              border={false}
                            >
                              <Link
                                to={applySubRoute(href)}
                                search={search}
                                className="w-full"
                              >
                                {title}
                              </Link>
                            </TabsTrigger>
                          )}
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TabsList>
            </ScrollArea>
            {row && (
              <div
                {...rowProps}
                className={cn(!isAlt && "pb-3 h-lg:pb-4", rowProps?.className)}
              >
                {render(row)}
              </div>
            )}
            {!isAlt && (
              <div
                className={cn(
                  "absolute bottom-[11px] h-lg:bottom-[15px] left-0 h-[1px] bg-border overflow-hidden",
                  nested
                    ? "w-[calc(100%+3.5rem)] -ml-10 -mr-4"
                    : "w-[calc(100%+5rem)] -ml-10 -mr-10",
                )}
              />
            )}
          </div>
        </div>
      )}
      {children !== undefined && (
        <CatchBoundary
          getResetKey={() => "reset"}
          errorComponent={ErrorComponent}
        >
          <TabsContent
            data-component="nav-tabs-content"
            ref={navTabsContentRef}
            {...content}
            tabIndex={undefined}
            className={cn(
              "@container/tabs-content relative",
              !padding && !isAlt && "-mt-3 h-lg:-mt-4",
              padding && isAlt && "pt-3",
              full
                ? !padding
                  ? "h-[calc(100%+0.75rem-var(--tabs-height))] h-lg:h-[calc(100%+1rem-var(--tabs-height))]"
                  : "h-[calc(100%-var(--tabs-height))]"
                : "",
              content?.className,
            )}
            value={selected!}
          >
            {children}
          </TabsContent>
        </CatchBoundary>
      )}
    </Tabs>
  );
}

export type NavTabPartialProps = Omit<NavTabProps, "header" | "root" | "items">;
